import {Component, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {DbServerErrors} from "../../shared/interceptors/db-server-errors";
import {ActivatedRoute} from '@angular/router';
import * as fromStore from "../../store";
import {Store} from '@ngrx/store';
import {Subject} from "rxjs";
import {Workspace} from "../../user-managment/workspace";
import {UserManagementService} from "../../user-managment/user-management.service";
import {DbResponse} from "../../shared/interceptors/db-response";
import {NotifierComponent} from "../../shared/components/notifier/notifier.component";
import {Notification} from "../../shared/components/notifier/notification";
import {NotificationType} from "../../shared/components/notifier/notification-type";
import {TypeUtils} from "../../shared/utils/type-utils";
import {filter, first, takeUntil} from 'rxjs/operators';



@Component({
    selector: 'db-workspace-settings',
    templateUrl: './workspace-settings.component.html',
    styleUrls: ['./workspace-settings.component.scss'],
    standalone: false
})
export class WorkspaceSettingsComponent implements OnInit, OnDestroy {

  public loading: boolean;

  public workspace: Workspace;
  public workspaceSettings: FormGroup;
  public serverErrors: DbServerErrors;
  public isMobile: boolean;
  public hasServerFieldErrors: boolean;

  private cacheIcon: any = null;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _route: ActivatedRoute,
    private _store: Store<fromStore.DbStoreState>,
    private _userManagementService: UserManagementService
  ) {}

  ngOnInit() {
    this.workspaceSettings = new FormGroup({
      'logo': new FormControl(''),
      'name': new FormControl('', [
        Validators.required,
        this.patternValidator(/[-a-zA-Z0-9@:%_+.~#?&\/=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&\/=]*)?/, { link: true }, true)
      ])
    });

    if (window.innerWidth < 991) {
      this.isMobile = true;
    }
    this.addEventHandlers();
  }

  private addEventHandlers(): void {
    this._store.select(fromStore.getOwnerWorkspace)
      .pipe(
        filter(workspace => !TypeUtils.isEmpty(workspace)),
        takeUntil(this._destroyStream$)
      )
      .subscribe(workspace => {
        this.workspace = workspace;
        this.refreshFormData(this.workspace);
      });

    this.workspaceSettings.get('name').valueChanges
      .pipe(
        takeUntil(this._destroyStream$),
        filter(value => value[0] === ' ')
      )
      .subscribe(value => {
        this.workspaceSettings.get('name').setValue(value.trim());
      });
  }

  ngOnDestroy(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private refreshFormData(data): void {
    this.workspaceSettings.setValue({
      'logo': data.workspaceLogo || '',
      'name': data.workspaceName
    }, {emitEvent: false});
  }

  public onWorkspaceUpdate(): void {
    if (this.workspaceSettings.invalid) return;
    this.serverErrors = null;
    if (this.workspaceSettings.valid) {
      this.loading = true;
      const data = {
        name: this.workspaceSettings.value.name.replace(/  +/g, ' ').trim()
      };
      if (this.cacheIcon) {
        Object.assign(data, {
          logo: this.cacheIcon
        });
      }
      this._userManagementService.updateWorkspaceData(data)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response: DbResponse): void => {
        this.loading = false;
        if (response.success) {
          this.workspace.workspaceName = this.workspaceSettings.value.name;
          if (this.workspaceSettings.value.logo) {
            this.workspace.workspaceLogo = this.workspaceSettings.value.logo;
          }
          this._store.dispatch(new fromStore.UpdateWorkspace(this.workspace));
          NotifierComponent.notify(new Notification(response.message, NotificationType.SUCCESS));
        }
        else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
      });
    }
  }

  public patternValidator(regex: RegExp, error: ValidationErrors, reverse = false): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null;
      }
      const valid = reverse ? !regex.test(control.value) : regex.test(control.value);
      return valid ? null : error;
    };
  }

  public onFileChanged(event): void {
    if (event.target.files && event.target.files[0]) {
      if (!event.target.files[0].type.includes('image/')) {
        NotifierComponent.notify(new Notification('File Type Is Not Allowed.', NotificationType.DANGER));
        return;
      }
      const file = event.target.files[0];

      const formData = new FormData();
      formData.append('logo_url', file);
      const reader = new FileReader();
      reader.onload = e => {
        this.workspaceSettings.get('logo').setValue(reader.result);
      };
      this.cacheIcon = file;
      reader.readAsDataURL(file);
    }
  }
}
